/**
 * The month grid behind the time-off day picker.
 *
 * 🪤 EVERY DATE IS A PLAIN `YYYY-MM-DD` STRING, compared as a string, and every
 * `Date` built from one is anchored at UTC midnight. A Runner in Charlotte
 * choosing "1 Oct" late on 30 September must get `2026-10-01`, not the day the
 * browser's local clock has already rolled to. `new Date('2026-10-01')` parses
 * as UTC; `new Date(2026, 9, 1)` does not, and that difference is the bug this
 * file exists to avoid.
 *
 * 🛑 Deliberately duplicated from the Runner app's `features/timeOff/model.ts`
 * rather than shared. The Runner app depends on no `@glplus/*` package
 * (isolation ruling 2026-08-24) and the portal cannot import from it, so the
 * two copies are the accepted cost. Change one, change the other — the tests on
 * both sides pin the same cases.
 */

export interface MonthCell {
  /** null for the leading and trailing blanks that pad the grid to full weeks. */
  date: string | null;
  day: number | null;
}

export function ymdOf(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function todayYmd(now = new Date()): string {
  return ymdOf(now);
}

/** Weeks of seven cells, MONDAY FIRST — the order the design's grid is drawn in. */
export function monthGrid(year: number, month1to12: number): MonthCell[][] {
  const first = new Date(Date.UTC(year, month1to12 - 1, 1));
  const daysInMonth = new Date(Date.UTC(year, month1to12, 0)).getUTCDate();
  // getUTCDay() is Sunday-0; this grid starts on Monday.
  const lead = (first.getUTCDay() + 6) % 7;
  const cells: MonthCell[] = [];
  for (let i = 0; i < lead; i += 1) cells.push({ date: null, day: null });
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push({ date: ymdOf(new Date(Date.UTC(year, month1to12 - 1, d))), day: d });
  }
  while (cells.length % 7 !== 0) cells.push({ date: null, day: null });
  const weeks: MonthCell[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/** "September 2026", in the viewer's language. */
export function monthTitle(year: number, month1to12: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(year, month1to12 - 1, 1)));
}

/** The seven weekday initials, Monday first, in the viewer's language. */
export function weekdayInitials(locale: string): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: 'narrow', timeZone: 'UTC' });
  // 2026-06-01 is a Monday, so seven days from it walk Mon→Sun.
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(Date.UTC(2026, 5, 1 + i))));
}

export function shiftMonth(year: number, month1to12: number, by: number): { year: number; month: number } {
  const d = new Date(Date.UTC(year, month1to12 - 1 + by, 1));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };
}

/** Every day from `a` to `b` inclusive, in order, whichever way round they came. */
export function rangeDays(a: string, b: string): string[] {
  const [from, to] = a <= b ? [a, b] : [b, a];
  const out: string[] = [];
  for (let d = new Date(`${from}T00:00:00.000Z`); ymdOf(d) <= to; d.setUTCDate(d.getUTCDate() + 1)) {
    out.push(ymdOf(d));
  }
  return out;
}

/** How many deliveries the chosen days already carry, for the picker's footer. */
export function deliveriesInRange(days: { date: string; deliveries: number }[], a: string, b: string): number {
  const inRange = new Set(rangeDays(a, b));
  return days.reduce((n, d) => (inRange.has(d.date) ? n + d.deliveries : n), 0);
}

/** Days that already carry deliveries, as a fast lookup for the dots. */
export function bookedDaySet(days: { date: string; deliveries: number }[]): ReadonlySet<string> {
  return new Set(days.filter((d) => d.deliveries > 0).map((d) => d.date));
}
